import React, { Component }  from "react"
import PropTypes              from "prop-types"
import { Link }               from "react-router-dom"
import _                      from "lodash"
import swal                   from "sweetalert"
import { observer }           from "mobx-react"
import { Col, Form, FormField, Row } from "elemental-react16"
import { Button, Card, Icon, Input, Segment } from "semantic-ui-react"
import shop                   from "shop/shop"
import app                    from "app.controller"

import ProductTable        from "components/Product/ProductTable"
import Typeahead           from "components/Typeahead/Typeahead"
import CustomProductOption from "components/CustomProductOption/CustomProductOption"
import Price               from "components/Price/Price"
import moment              from "moment"
import ProductImporter     from "components/Product/ProductImporter"
import PrintCollectionButton from "components/PrintCollectionButton"

import "./OrderlistPage.scss"

// ─── Page ─────────────────────────────────────────────────────────────────────

// @observer makes MobX track svc.lists / svc.listIds and re-render automatically
// once hydrate() finishes, without relying on Signal timing.
@observer
export default class OrderlistPageV2 extends Component {
  state = {
    id: this.props.match.params.id
  }

  componentDidUpdate( prevProps ) {
    const nextId = this.props.match.params.id
    if ( prevProps.match.params.id !== nextId ) {
      this.setState( { id: nextId } )
    }
  }

  createList = ( title, products = [] ) => {
    shop.orderlistV2.createList( title, products )
  }

  removeList = ( id ) => {
    shop.orderlistV2.removeList( id )
  }

  handleSelect = ( { product } ) => {
    if ( !product || !product.map_id ) return
    const { id } = this.state
    const list   = shop.orderlistV2.lists[ id ]
    if ( !list ) return
    list.add( product )
    list.save()
  }

  handleChange = ( { product } ) => {
    const { id } = this.state
    const list   = shop.orderlistV2.lists[ id ]
    if ( !list ) return
    list.add( product )
    list.save()
  }

  render() {
    const { id }  = this.state
    const svc     = shop.orderlistV2
    const list    = id && svc ? svc.lists[ id ] : null
    return (
      <div className="container pb5 pt5">

        {/* ── LIST OVERVIEW ── */}
        {!id && (
          <div>
            <h1>{translate( "Orderlist" )}</h1>

            <section>
              <Row>
                <Col sm="1/3">
                  <CreateListForm handleCreate={this.createList}/>
                </Col>
              </Row>
            </section>

            <section className="pb5 bb b--black-10">
              <ListsGrid
                lists={svc ? svc.lists : {}}
                listIds={svc ? svc.listIds : []}
                handleRemove={this.removeList}
              />
            </section>

            <Segment>
              <ProductImporter onSuccess={( products, { name } ) => {
                this.createList( name, products )
                return true
              }}/>
            </Segment>
          </div>
        )}

        {/* ── LIST DETAIL ── */}
        {id && list && (
          <Row>
            <Col sm="1" md="3/4">
              <section>
                <div className="row-title title--left">
                  <h2>{translate( "add" )}</h2>
                </div>
                <Typeahead
                  fluid
                  optionComponent={CustomProductOption}
                  onSelect={this.handleSelect}
                  value={this.state.value}
                />
              </section>
            </Col>

            <section className="padding-bottom db pt4" style={{ width: "100%" }}>
              <Col sm="1">
                <div className="row-title title--left">
                  <div className="flex flex-row justify-center items-center">
                    <div className="f3 ttu">
                      <span className="dib mr3">{list.customName || list.title}</span>
                    </div>
                    <Button
                      basic
                      onClick={() => {
                        swal( {
                          title           : translate( "Name" ),
                          text            : translate( "Merkliste umbenennen" ),
                          type            : "input",
                          showCancelButton: true,
                          cancelButtonText: translate( "Cancel" ),
                          closeOnConfirm  : false,
                          animation       : "slide-from-top",
                          inputPlaceholder: list.customName || list.title
                        }, inputValue => {
                          if ( inputValue === false ) return false
                          if ( !inputValue || inputValue.trim() === "" ) {
                            swal.showInputError( translate( "ungültiger Name" ) )
                            return false
                          }
                          list.customName = inputValue.trim()
                          list.save()
                          swal( list.customName, "List: " + list.customName, "success" )
                        } )
                      }}
                    >
                      {translate( "umbenennen" )}
                    </Button>
                  </div>
                </div>

                <ListDetail list={list} onChange={this.handleChange}/>
              </Col>
            </section>

            <div className="w-100 flex flex-row justify-end">
              {shop.config.usePDFExport && (
                <div className="pr2">
                  <PrintCollectionButton products={Object.values( list.products )}/>
                </div>
              )}

              <div className="pr2">
                <Button
                  onClick={() => {
                    if ( !list ) return
                    shop.cart.export( {
                      products: Object.values( list.products ),
                      filename: `${list.title}-${moment().format( "YYYY-MM-DD--HH-mm" )}`
                    } )
                  }}
                  basic
                >
                  <Icon name="download"/> {translate( "Exportieren" )}
                </Button>
              </div>

              <Button
                primary
                onClick={() => {
                  if ( !list ) return
                  list.listToCart()
                  shop.toast( translate( "toast.LIST_TO_CART" ), "success" )
                  setTimeout( () => app.sidebar.toggle.dispatch(), 220 )
                }}
              >
                <Icon name="cart"/> {translate( "add all" )}
              </Button>
            </div>
          </Row>
        )}

        {/* List-ID in URL but list not found (e.g. just deleted) */}
        {id && !list && (
          <div className="tc pt5">
            <p>{translate( "Liste nicht gefunden." )}</p>
            <Link to="/orderlist">
              <Button basic>{translate( "Alle Merklisten" )}</Button>
            </Link>
          </div>
        )}
      </div>
    )
  }
}

// ─── Create-List Form ─────────────────────────────────────────────────────────

class CreateListForm extends Component {
  state = { name: "" }

  handleChange = e => this.setState( { name: e.target.value } )

  render() {
    const { handleCreate } = this.props
    return (
      <Form
        className="pb2"
        onSubmit={e => {
          e.preventDefault()
          if ( this.state.name.length >= 3 ) {
            handleCreate( this.state.name )
            this.setState( { name: "" } )
          } else {
            shop.toast( translate( "Der Merklisten-Titel sollte mindestens 3 Zeichen beinhalten" ), "warning" )
          }
        }}
      >
        <FormField htmlFor="v2-list-name-input">
          <Input
            id="v2-list-name-input"
            value={this.state.name}
            onChange={this.handleChange}
            action={<Button type="submit">{translate( "create" )}</Button>}
            placeholder={translate( "name" )}
          />
        </FormField>
      </Form>
    )
  }
}

// ─── List Grid (overview cards) ───────────────────────────────────────────────

const ListsGrid = ( { lists, listIds, handleRemove } ) => (
  <Card.Group itemsPerRow={4} stackable>
    {listIds.map( id => {
      const list = lists[ id ]
      if ( !list || list.isArchived ) return null
      return (
        <ListCard
          key={id}
          list={list}
          handleRemove={handleRemove}
        />
      )
    } )}
  </Card.Group>
)
ListsGrid.propTypes = {
  lists      : PropTypes.object,
  listIds    : PropTypes.array,
  handleRemove: PropTypes.func
}

// ─── List Card ────────────────────────────────────────────────────────────────

@observer
class ListCard extends Component {
  constructor( props ) {
    super( props )
    const { list } = props
    list.events.change.add( this.onChange )
  }

  onChange = () => {
    this.forceUpdate()
  }

  componentWillUnmount() {
    this.props.list.events.change.remove( this.onChange )
  }

  render() {
    const { list, handleRemove }  = this.props
    const storeV2                 = shop.store.orderlistV2
    const isCurrent               = storeV2 && storeV2.currentListId === list.id

    return (
      <Card>
        <Card.Content>
          <Link to={`/orderlist/${list.id}`}>
            <h2>{list.customName || list.title}</h2>
            <div>
              <strong><Price>{list.sum}</Price></strong><br/>
              {list.size > 0 && (
                <span><strong>{list.size}</strong> {translate( "Produkte" )}</span>
              )}
            </div>
          </Link>
        </Card.Content>

        <Card.Meta>
          <div className="pa3 text-right bt b--black-10">
            <div className="fl">
              <Button
                title="Standard"
                icon="star"
                basic={!isCurrent}
                size="small"
                primary={isCurrent}
                onClick={() => {
                  if ( storeV2 ) storeV2.setCurrentList( isCurrent ? null : list )
                }}
              />
            </div>

            <Button
              basic
              size="small"
              icon="trash"
              onClick={() =>
                swal( {
                  title            : translate( "Löschen?" ),
                  text             : `"${list.customName || list.title}"`,
                  type             : "warning",
                  showCancelButton : true,
                  animation        : false,
                  confirmButtonText: translate( "Eintrag löschen" ),
                  cancelButtonText : translate( "Zurück" ),
                  closeOnConfirm   : true
                }, () => handleRemove( list.id ) )
              }
            />

            <Button
              basic
              size="small"
              primary
              icon="cart"
              onClick={() => {
                list.listToCart()
                shop.toast( translate( "toast.LIST_TO_CART" ), "success" )
              }}
            />
          </div>
        </Card.Meta>
      </Card>
    )
  }
}
ListCard.propTypes = {
  list        : PropTypes.object,
  handleRemove: PropTypes.func
}

// ─── List Detail (product table) ─────────────────────────────────────────────

@observer
class ListDetail extends Component {
  constructor( props ) {
    super( props )
    const { list } = props
    if ( list ) list.events.change.add( this.handleChange )
  }

  handleChange = () => {
    this.forceUpdate()
  }

  componentWillUnmount() {
    const { list } = this.props
    if ( list ) list.events.change.remove( this.handleChange )
  }

  render() {
    const { list, onChange } = this.props
    return (
      <div className="cf">
        <ProductTable key={list.hash} onChange={onChange} list={list} sort={true}/>
        <br/>
      </div>
    )
  }
}
ListDetail.propTypes = {
  list    : PropTypes.object,
  onChange: PropTypes.func
}



// WEBPACK FOOTER //
// ./src/theme/hvz/pages/orderlist/OrderlistPageV2.js